import React, { useState, useMemo } from "react";
import { Button, Input, Table, Tooltip } from "antd";
import TableTitle from "../../../../../../common/TableTitle";
import { appTheme } from "../../../../../../../config/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { SearchHeader } from "../../../../../../layout/header/HeaderList";

const MenuTabs = ["Bilan Actifs", "Bilan Passifs", "Compte Résultat", "SIG"];

const HistoriqueExamenAnalytique = ({
  sigTabHistory,
  chargement,
  mission,
  actifHistory,
  passifHistory,
  resultHistory,
  setActifHistory,
  setPassifHistory,
  setResultHistory,
  setSigTabHistory,
  setIsTaskSave,
  handleSubmit,
}) => {
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // ✅ État pour le terme de recherche
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 100,
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const handleTabChange = (tabName) => {
    setLoading(true);
    // ✅ Réinitialiser la recherche lors du changement d'onglet
    setSearchTerm("");
    setTimeout(() => {
      setActiveTab(tabName);
      setLoading(false);
    }, 1000);
  };

  // ✅ Fonction de recherche corrigée avec gestion par onglet
  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setSearchTerm(value);
  };

  // ✅ Données filtrées par onglet avec useMemo pour la performance
  const filteredData = useMemo(() => {
    const filterItems = (items) => {
      if (!searchTerm) return items;

      return items.filter((item) => {
        const libelleMatch = item.libelle?.toLowerCase().includes(searchTerm);
        const refMatch = item.compte
          ?.toString()
          .toLowerCase()
          .includes(searchTerm);
        const commentaireMatch = item.commentaire
          ?.toLowerCase()
          .includes(searchTerm);
        return libelleMatch || refMatch || commentaireMatch;
      });
    };

    return {
      actifs: filterItems(actifHistory || []),
      passifs: filterItems(passifHistory || []),
      resultats: filterItems(resultHistory || []),
      sig: filterItems(sigTabHistory || []),
    };
  }, [searchTerm, actifHistory, passifHistory, resultHistory, sigTabHistory]);

  // ✅ Fonction utilitaire pour le formatage des montants
  const formatMontant = (montant) => {
    return (montant || 0).toLocaleString("fr");
  };

  // ✅ Fonction utilitaire pour calculer les variations
  const calculateVariation = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // ✅ Fonction utilitaire pour les classes de variation
  const getVariationClass = (taux) => {
    if (Math.abs(taux) >= 10) return "bg-danger text-white";
    if (Math.abs(taux) >= 5) return "bg-warning text-dark";
    return "";
  };

  const columnsActifs = [
    {
      title: () => <TableTitle>#</TableTitle>,
      render: (_, record, index) => (
        <span className="text-muted fw-semibold">{index + 1}</span>
      ),
      width: 50,
      fixed: "left",
    },
    {
      title: () => <TableTitle>Référence</TableTitle>,
      dataIndex: "compte",
      width: 100,
      fixed: "left",
      render: (_, record) => (
        <span className="fw-bold text-primary" style={{ fontSize: "14px" }}>
          {record.compte}
        </span>
      ),
    },
    {
      title: () => <TableTitle>Libellé</TableTitle>,
      dataIndex: "libelle",
      width: 350,
      render: (_, record) => (
        <div className="d-flex flex-column">
          <span className="fw-bold" style={{ fontSize: "14px" }}>
            {record.libelle}
          </span>
        </div>
      ),
      ellipsis: true,
    },
    {
      title: () => <TableTitle>Exercice {mission.annee || "N"}</TableTitle>,
      dataIndex: "soldeN",
      width: 130,
      align: "right",
      render: (_, record) => (
        <div className="text-end fw-bold" style={{ fontSize: "14px" }}>
          {formatMontant(record.soldeN)}
        </div>
      ),
    },
    {
      title: () => (
        <TableTitle>Exercice {parseInt(mission.annee) - 1 || "N-1"}</TableTitle>
      ),
      dataIndex: "soldeN_1",
      width: 130,
      align: "right",
      render: (_, record) => (
        <div className="text-end fw-bold" style={{ fontSize: "14px" }}>
          {formatMontant(record.soldeN_1)}
        </div>
      ),
    },
    {
      title: () => <TableTitle>N - N-1</TableTitle>,
      align: "center",
      children: [
        {
          title: () => <TableTitle>Variation</TableTitle>,
          width: 130,
          align: "right",
          render: (_, record) => {
            const variation = record.soldeN - (record.soldeN_1 || 0);
            const taux = calculateVariation(record.soldeN, record.soldeN_1);

            return (
              <div
                className={`text-end p-2 fw-bold rounded ${getVariationClass(
                  taux
                )}`}
                style={{ fontSize: "13px" }}
              >
                {formatMontant(variation)}
              </div>
            );
          },
        },
        {
          title: () => <TableTitle>Taux (%)</TableTitle>,
          width: 100,
          align: "right",
          render: (_, record) => {
            const taux = calculateVariation(record.soldeN, record.soldeN_1);

            return (
              <div
                className={`text-end p-2 fw-bold rounded ${getVariationClass(
                  taux
                )}`}
                style={{ fontSize: "13px" }}
              >
                {taux.toFixed(2)}%
              </div>
            );
          },
        },
      ],
    },
    {
      title: () => <TableTitle>COMMENTAIRE</TableTitle>,
      dataIndex: "commentaire",
      width: 400,
      render: (_, record, index) => (
        <div className="mb-3">
          <Input.TextArea
            style={{ fontSize: "12px" }}
            size="large"
            bordered={false}
            placeholder="Saisir le commentaire..."
            value={record.commentaire}
            maxLength={2000}
            showCount
            autoSize={{
              minRows: 1,
              maxRows: 4,
            }}
            onChange={(e) => {
              const updatedList = actifHistory.map((item, i) =>
                i === index ? { ...item, commentaire: e.target.value } : item
              );
              setActifHistory(updatedList);
              setIsTaskSave((prevState) => [...prevState, 1]);
            }}
          />
        </div>
      ),
    },
  ];

  // ✅ Colonnes similaires pour les autres onglets (Passifs, Résultats, SIG)
  const columnsPassif = [
    ...columnsActifs.map((col) => ({
      ...col,
      render:
        col.dataIndex === "commentaire"
          ? (_, record, index) => (
              <div className="mb-3">
                <Input.TextArea
                  style={{ fontSize: "12px" }}
                  size="large"
                  bordered={false}
                  placeholder="Saisir le commentaire..."
                  value={record.commentaire}
                  maxLength={2000}
                  showCount
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onChange={(e) => {
                    const updatedList = passifHistory.map((item, i) =>
                      i === index
                        ? { ...item, commentaire: e.target.value }
                        : item
                    );
                    setPassifHistory(updatedList);
                    setIsTaskSave((prevState) => [...prevState, 1]);
                  }}
                />
              </div>
            )
          : col.render,
    })),
  ];

  const columnsResultats = [
    ...columnsActifs.map((col) => ({
      ...col,
      render:
        col.dataIndex === "commentaire"
          ? (_, record, index) => (
              <div className="mb-3">
                <Input.TextArea
                  style={{ fontSize: "12px" }}
                  size="large"
                  bordered={false}
                  placeholder="Saisir le commentaire..."
                  value={record.commentaire}
                  maxLength={2000}
                  showCount
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onChange={(e) => {
                    const updatedList = resultHistory.map((item, i) =>
                      i === index
                        ? { ...item, commentaire: e.target.value }
                        : item
                    );
                    setResultHistory(updatedList);
                    setIsTaskSave((prevState) => [...prevState, 1]);
                  }}
                />
              </div>
            )
          : col.render,
    })),
  ];

  const columnsSIG = [
    ...columnsActifs.map((col) => ({
      ...col,
      render:
        col.dataIndex === "commentaire"
          ? (_, record, index) => (
              <div className="mb-3">
                <Input.TextArea
                  style={{ fontSize: "12px" }}
                  size="large"
                  bordered={false}
                  placeholder="Saisir le commentaire..."
                  value={record.commentaire}
                  maxLength={2000}
                  showCount
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onChange={(e) => {
                    const updatedList = sigTabHistory.map((item, i) =>
                      i === index
                        ? { ...item, commentaire: e.target.value }
                        : item
                    );
                    setSigTabHistory(updatedList);
                    setIsTaskSave((prevState) => [...prevState, 1]);
                  }}
                />
              </div>
            )
          : col.render,
    })),
  ];

  return (
    <>
      <div className="py-2">
        <div className="d-flex justify-content-end align-items-end">
          <Tooltip title="Résultats">
            <Button
              disabled={
                mission.isCloture === 1 || mission.isClotureProvisoire === 1
              }
              size={"large"}
              style={{
                backgroundColor: appTheme.colors.primary,
                color: "white",
              }}
              icon={
                <FontAwesomeIcon style={{ paddingRight: "10" }} icon={faSave} />
              }
              onClick={handleSubmit}
            >
              {!chargement && (
                <span className="indicator-label text-white">Enregistrer</span>
              )}
              {chargement && (
                <span
                  className="indicator-progress fs-9 text-white"
                  style={{ display: "block" }}
                >
                  Patientez svp...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </Button>
          </Tooltip>
        </div>

        <div className="flex-lg-row-fluid">
          <div className="d-flex flex-column align-center">
            <ul className="nav nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-4 fw-bold mb-3">
              {(MenuTabs ?? []).map((menu, index) => (
                <li key={index} className="nav-item">
                  <button
                    style={{ fontWeight: "600", fontFamily: "Oswald" }}
                    className={`nav-link text-active-primary text-uppercase pb-4 fs-6 ${
                      activeTab === index + 1 ? "active" : ""
                    }`}
                    onClick={() => handleTabChange(index + 1)}
                  >
                    {menu}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ✅ Barre de recherche */}
          <div style={{ maxWidth: "300px", width: "100%" }}>
            <SearchHeader
              placeholder="Rechercher par référence, libellé ou commentaire..."
              handleSearch={handleSearchChange}
              value={searchTerm}
            />
          </div>

          <form className="stepper-form">
            <div className="tab-content" id="myTabContent">
              {/* ✅ Onglet Bilan Actifs */}
              <div
                className={`tab-pane fade ${
                  activeTab === 1 ? "show active" : ""
                }`}
                role="tabpanel"
              >
                <div className="mb-10">
                  <div className="table-responsive">
                    <Table
                      rowClassName={() => `h-3px p-0 m-0 lh-0.5`}
                      columns={columnsActifs}
                      rowKey={(record, index) => record.id || index}
                      dataSource={filteredData.actifs}
                      pagination={tableParams.pagination}
                      scroll={{ x: 1300 }}
                      onChange={handleTableChange}
                      loading={chargement || loading}
                      size={"small"}
                    />
                  </div>
                </div>
              </div>

              {/* ✅ Onglet Bilan Passifs */}
              <div
                className={`tab-pane fade ${
                  activeTab === 2 ? "show active" : ""
                }`}
                role="tabpanel"
              >
                <div className="mb-10">
                  <div className="table-responsive">
                    <Table
                      rowClassName={() => `h-3px p-0 m-0 lh-0.5`}
                      columns={columnsPassif}
                      rowKey={(record, index) => record.id || index}
                      dataSource={filteredData.passifs}
                      pagination={tableParams.pagination}
                      scroll={{ x: 1300 }}
                      onChange={handleTableChange}
                      loading={chargement || loading}
                      size={"small"}
                    />
                  </div>
                </div>
              </div>

              {/* ✅ Onglet Compte Résultat */}
              <div
                className={`tab-pane fade ${
                  activeTab === 3 ? "show active" : ""
                }`}
                role="tabpanel"
              >
                <div className="mb-10">
                  <div className="table-responsive">
                    <Table
                      rowClassName={() => `h-3px p-0 m-0 lh-0.5`}
                      columns={columnsResultats}
                      rowKey={(record, index) => record.id || index}
                      dataSource={filteredData.resultats}
                      pagination={tableParams.pagination}
                      scroll={{ x: 1300 }}
                      onChange={handleTableChange}
                      loading={chargement || loading}
                      size={"small"}
                    />
                  </div>
                </div>
              </div>

              {/* ✅ Onglet SIG */}
              <div
                className={`tab-pane fade ${
                  activeTab === 4 ? "show active" : ""
                }`}
                role="tabpanel"
              >
                <div className="mb-10">
                  <div className="table-responsive">
                    <Table
                      rowClassName={() => `h-3px p-0 m-0 lh-0.5`}
                      columns={columnsSIG}
                      rowKey={(record, index) => record.id || index}
                      dataSource={filteredData.sig}
                      pagination={tableParams.pagination}
                      scroll={{ x: 1300 }}
                      onChange={handleTableChange}
                      loading={chargement || loading}
                      size={"small"}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export { HistoriqueExamenAnalytique };
